// Use cases — industry / persona landing pages. Shared content model.

export const USECASES = [
  {
    slug: "agencies",
    category: "Use case",
    tone: "candy",
    eyebrow: "FOR AGENCIES",
    title: "Link management for agencies",
    excerpt:
      "Run every client's links, QR, and campaigns from one platform — with branded links, per-campaign analytics, and clean reporting your clients can read.",
    readTime: "6 min",
    date: "2026-05-09",
    updated: "2026-06-03",
    seo: {
      title: "Link Management for Agencies | LinkLane",
      description:
        "LinkLane for agencies: branded short links, per-campaign analytics, branded QR, smart routing, and CSV-ready reporting across all your clients.",
      keywords: [
        "link management for agencies",
        "agency link tracking",
        "campaign tracking agency",
        "branded links agency",
      ],
    },
    sections: [
      {
        type: "intro",
        body: "Agencies live and die by attribution. Clients want to know which channel, post, or ad drove results — and they want it in a report they can actually read. LinkLane gives you consistent UTM-tagged short links, per-campaign rollups, and exports, so reporting takes minutes, not afternoons.",
      },
      {
        type: "feature-grid",
        items: [
          {
            title: "Per-campaign rollups",
            body: "Group every client link into a campaign and read clicks, uniques, and breakdowns in one view.",
            tone: "candy",
          },
          {
            title: "Consistent UTMs",
            body: "Store UTM values per link so casing and naming never fracture a client's report.",
            tone: "mint",
          },
          {
            title: "Branded QR",
            body: "Print-ready, logo'd QR for client events and packaging — every scan tracked.",
            tone: "pink",
          },
          {
            title: "CSV export",
            body: "One-click export to drop into client decks and dashboards.",
            tone: "yellow",
          },
        ],
      },
      { type: "h2", title: "Built for client work" },
      {
        type: "list",
        items: [
          "Smart routing (geo, device, A/B) to send the right audience to the right landing page.",
          "Custom domains on Enterprise so links carry the client's brand, not a generic host.",
          "Team seats (Enterprise) to give account managers their own access.",
          "Real-time click feed for launch-day monitoring during big pushes.",
        ],
      },
      {
        type: "faq",
        title: "Agencies FAQ",
        items: [
          {
            q: "Can I manage multiple clients' links?",
            a: "Yes — use campaigns to separate and roll up each client's links, with per-campaign analytics and CSV export for reporting.",
          },
          {
            q: "Can links use a client's own domain?",
            a: "Yes, on the Enterprise plan, with DNS-verified custom domains.",
          },
          {
            q: "Is there team access?",
            a: "Team seats are available on Enterprise so account managers each get their own login.",
          },
        ],
      },
      {
        type: "cta",
        eyebrow: "AGENCIES",
        title: "Reporting in minutes.",
        to: "/signup",
        label: "Start free",
      },
    ],
    faq: [
      {
        q: "Is LinkLane good for agencies?",
        a: "Yes — per-campaign rollups, consistent UTMs, branded QR, custom domains, and CSV export make client link management and reporting fast.",
      },
    ],
    related: ["guides:utm-tracking", "compare:linklane-vs-rebrandly", "use-cases:saas"],
  },

  {
    slug: "creators",
    category: "Use case",
    tone: "pink",
    eyebrow: "FOR CREATORS",
    title: "LinkLane for creators",
    excerpt:
      "One themeable bio page, branded QR for your merch and IRL drops, and analytics that finally tell you which link your audience actually taps.",
    readTime: "5 min",
    date: "2026-05-07",
    updated: "2026-06-03",
    seo: {
      title: "LinkLane for Creators: Bio, QR & Analytics",
      description:
        "Creators get a themeable link-in-bio page, branded QR codes, and unified real-time analytics — see which links convert and grow on purpose.",
      keywords: [
        "link in bio for creators",
        "creator link tool",
        "bio page creator",
        "linktree alternative creators",
      ],
    },
    sections: [
      {
        type: "intro",
        body: "As a creator your bio link is your storefront, your press kit, and your call-to-action all at once. LinkLane gives you a bio page you can actually brand, branded QR for merch and events, and one analytics view that tells you what's working — so you can double down on it.",
      },
      {
        type: "feature-grid",
        items: [
          {
            title: "Themeable bio page",
            body: "Theme Studio: colors, fonts, button styles, background — match your feed.",
            tone: "pink",
          },
          {
            title: "Drag to reorder",
            body: "Put your current priority on top in seconds.",
            tone: "candy",
          },
          {
            title: "Branded QR",
            body: "For merch, packaging, and IRL — each scan tracked as a click.",
            tone: "mint",
          },
          {
            title: "Unified analytics",
            body: "Bio taps + link clicks in one real-time view.",
            tone: "yellow",
          },
        ],
      },
      {
        type: "faq",
        title: "Creators FAQ",
        items: [
          {
            q: "Can I match my bio page to my brand?",
            a: "Yes — Theme Studio customizes colors, fonts, button styles, and background, with a live mobile preview.",
          },
          {
            q: "Do I get analytics on a free plan?",
            a: "The free plan includes a bio page and basic stats; Pro unlocks the full real-time analytics, more themes, and branded QR.",
          },
        ],
      },
      {
        type: "cta",
        eyebrow: "CREATORS",
        title: "Grow on purpose.",
        to: "/signup",
        label: "Build your bio page",
      },
    ],
    faq: [
      {
        q: "Is LinkLane good for creators?",
        a: "Yes — a themeable bio page, branded QR, and unified analytics in one tool help creators see what converts and grow intentionally.",
      },
    ],
    related: [
      "guides:link-in-bio-strategy",
      "alternatives:best-linktree-alternatives",
      "compare:linklane-vs-linktree",
    ],
  },

  {
    slug: "saas",
    category: "Use case",
    tone: "mint",
    eyebrow: "FOR SAAS TEAMS",
    title: "Link tracking for SaaS teams",
    excerpt:
      "Track every acquisition channel, A/B-route landing pages, and wire link events into your stack with a REST API, webhooks, and Slack alerts.",
    readTime: "6 min",
    date: "2026-05-05",
    updated: "2026-06-03",
    seo: {
      title: "Link Tracking for SaaS Teams | LinkLane",
      description:
        "LinkLane for SaaS: channel attribution, A/B and geo routing, a REST API with scoped keys, HMAC webhooks, and Slack/Discord alerts for link events.",
      keywords: [
        "link tracking for saas",
        "saas attribution",
        "ab test links",
        "link api webhooks",
      ],
    },
    sections: [
      {
        type: "intro",
        body: "Growth teams need attribution they trust and infrastructure they can automate. LinkLane gives you consistent UTM-tagged links, A/B and geo routing on a single URL, and a developer platform — REST API with scoped keys, HMAC-signed webhooks, and Slack/Discord alerts — so link events flow into the rest of your stack.",
      },
      {
        type: "feature-grid",
        items: [
          {
            title: "A/B & geo routing",
            body: "Split traffic or send regions to the right page from one link.",
            tone: "mint",
          },
          {
            title: "REST API + keys",
            body: "Scoped API keys (links / analytics) for programmatic control.",
            tone: "candy",
          },
          {
            title: "HMAC webhooks",
            body: "Signed events for clicks, creates, updates, and deletes.",
            tone: "pink",
          },
          {
            title: "Slack / Discord",
            body: "Pipe link events straight into your team channels.",
            tone: "yellow",
          },
        ],
      },
      { type: "h2", title: "Automate the boring parts" },
      {
        type: "ol",
        items: [
          "Create campaign links programmatically via the API at launch time.",
          "Subscribe a webhook to link.clicked to stream conversions into your warehouse.",
          "Alert #growth in Slack when a launch link crosses a click threshold.",
          "Read per-link and account-wide analytics to compare channels.",
        ],
      },
      {
        type: "faq",
        title: "SaaS FAQ",
        items: [
          {
            q: "Does LinkLane have an API?",
            a: "Yes — a REST API with scoped keys (links / analytics) on the Enterprise plan. Webhooks and Slack/Discord integrations are available on Pro+.",
          },
          {
            q: "Can I A/B test landing pages with a link?",
            a: "Yes — A/B routing splits clicks across weighted variants from a single short link; results show in analytics by variant.",
          },
          {
            q: "Are webhooks signed?",
            a: "Yes — webhooks are HMAC-signed and cover link.clicked, link.created, link.updated, and link.deleted.",
          },
        ],
      },
      {
        type: "cta",
        eyebrow: "SAAS",
        title: "Attribution + automation.",
        to: "/signup",
        label: "Start free",
      },
    ],
    faq: [
      {
        q: "Is LinkLane good for SaaS growth teams?",
        a: "Yes — channel attribution, A/B and geo routing, a REST API with scoped keys, HMAC webhooks, and Slack/Discord alerts cover acquisition tracking and automation.",
      },
    ],
    related: ["guides:utm-tracking", "compare:linklane-vs-dub", "use-cases:agencies"],
  },

  {
    slug: "events",
    category: "Use case",
    tone: "yellow",
    eyebrow: "FOR EVENTS",
    title: "QR codes & links for events",
    excerpt:
      "Branded QR for booths, badges, and signage — with editable destinations and real-time scan analytics so you know what worked while the event's still on.",
    readTime: "5 min",
    date: "2026-05-03",
    updated: "2026-06-03",
    seo: {
      title: "QR Codes & Link Tracking for Events",
      description:
        "LinkLane for events: branded, editable QR codes for booths and signage, real-time scan analytics, and smart routing for multi-region attendees.",
      keywords: [
        "qr codes for events",
        "event link tracking",
        "conference qr code",
        "trackable event qr",
      ],
    },
    sections: [
      {
        type: "intro",
        body: "Events are where offline meets online — and where measurement usually breaks. Branded, trackable QR codes fix that: print them on booths, badges, and signage, and watch scans land in real time. Because each QR points at an editable short link, you can change the destination mid-event without reprinting a thing.",
      },
      {
        type: "feature-grid",
        items: [
          {
            title: "Editable QR",
            body: "Change the destination after printing — no reprint.",
            tone: "yellow",
          },
          {
            title: "Real-time scans",
            body: "See which booth or banner drove traffic as it happens.",
            tone: "mint",
          },
          {
            title: "Branded codes",
            body: "Logo, colors, and frames so codes look trustworthy.",
            tone: "candy",
          },
          {
            title: "Smart routing",
            body: "Send attendees by region or device to the right page.",
            tone: "pink",
          },
          {
            title: "CTA overlays",
            body: "Greet scanners with a branded banner and an offer before the redirect.",
            tone: "candy",
          },
        ],
      },
      {
        type: "faq",
        title: "Events FAQ",
        items: [
          {
            q: "Can I change a printed QR's destination during the event?",
            a: "Yes — the QR points at a short link, so updating the link's destination instantly redirects every printed code.",
          },
          {
            q: "Can I see scans in real time?",
            a: "Yes — scans are clicks with a 'qr' source and appear in the real-time feed with device, country, and time breakdowns.",
          },
        ],
      },
      {
        type: "cta",
        eyebrow: "EVENTS",
        title: "Measure the room.",
        to: "/signup",
        label: "Make event QR codes",
      },
    ],
    faq: [
      {
        q: "Are QR codes for events trackable?",
        a: "Yes — LinkLane event QR codes point at short links, so every scan is a tracked click visible in real-time analytics, and destinations stay editable after printing.",
      },
    ],
    related: ["guides:qr-codes-for-marketing", "use-cases:agencies", "guides:utm-tracking"],
  },
];

export const USECASES_BY_SLUG = Object.fromEntries(USECASES.map((e) => [e.slug, e]));
